export interface EnvironmentFeatureRequirements {
  requireNativeKafka?: boolean
  requireSharedVkc?: boolean
}

interface EnvironmentWithFeatures {
  features?: {
    nativeKafka?: boolean
    sharedVkc?: boolean
  }
}

interface LocationWithIdentity {
  pool: {
    metadata?: {
      name?: string
      namespace?: string
    }
  }
  environment: {
    region?: string
  }
}

const isNativeKafkaEnabled = (environment: EnvironmentWithFeatures) => {
  return environment.features?.nativeKafka === true
}

export const isServerlessVkcEnabled = (environment: EnvironmentWithFeatures) => {
  return isNativeKafkaEnabled(environment) && environment.features?.sharedVkc === true
}

export const hasRequiredEnvironmentFeatures = (
  environment: EnvironmentWithFeatures,
  requirements?: EnvironmentFeatureRequirements
) => {
  if (requirements?.requireSharedVkc) {
    return isServerlessVkcEnabled(environment)
  }
  if (requirements?.requireNativeKafka) {
    return isNativeKafkaEnabled(environment)
  }
  return true
}

export const isServerlessLocationAvailable = (
  locationId: string,
  environment: EnvironmentWithFeatures,
  configuredLocationIds: string[],
  requirements?: EnvironmentFeatureRequirements
) => {
  if (!hasRequiredEnvironmentFeatures(environment, requirements)) {
    return false
  }

  // Serverless Kafka availability is projected per environment through PoolOption status.
  // The configured location list remains the source of truth for serverless Pulsar.
  return requirements?.requireSharedVkc === true || configuredLocationIds.includes(locationId)
}

export const resolveAvailableLocation = <T extends LocationWithIdentity>(
  selectedLocation: T | undefined,
  availableLocations: T[]
) => {
  if (!selectedLocation) {
    return availableLocations[0]
  }

  return (
    availableLocations.find(location => {
      return (
        location.pool.metadata?.name === selectedLocation.pool.metadata?.name &&
        location.pool.metadata?.namespace === selectedLocation.pool.metadata?.namespace &&
        location.environment.region === selectedLocation.environment.region
      )
    }) ?? availableLocations[0]
  )
}
